import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import BrandKeylineBackground from '@/components/atoms/BrandKeylineBackground/BrandKeylineBackground';
import Button from '@/components/atoms/Button/Button';
import SectionWrapper from '@/components/atoms/SectionWrapper/SectionWrapper';
import Typography from '@/components/atoms/Typography/Typography';
import Carousel from '@/components/molecules/Carousel/Carousel';
import SegmentedControl from '@/components/molecules/SegmentedControl/SegmentedControl';
import { dataLayerPush } from '@/helpers/dataLayer';
import { focusFirstFocusableIn } from '@/helpers/focusUtils';
import { useIsClient } from '@/hooks/useIsClient';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchCard } from './SearchCard';
import SearchNoResults from './SearchNoResults';
import SearchSection from './SearchSection';
const SearchListing = ({ centreTheme, theme, componentID, title, searchSections = [], hideBrandKeylines, searchEndpoint, topPadding, bottomPadding, anchorNavTitle, noResult, showAllLabel, allResult, parentPage, }) => {
    const [activeTab, setActiveTab] = useState(null);
    const [activeSection, setActiveSection] = useState(null);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const isClient = useIsClient();
    const query = isClient ? new URLSearchParams(window.location.search).get('q') || '' : '';
    const [isShowAllFocused, setIsShowAllFocused] = useState(false);
    const updateHash = useCallback((tabIndex) => {
        const url = new URL(window.location.href);
        url.hash = `#tab=${tabIndex}`;
        window.history.replaceState(null, '', url.toString());
    }, []);
    const processedTabs = useMemo(() => {
        if (searchSections.some((section) => !section.initialData))
            return [];
        return [
            {
                ...(allResult || {
                    // Default values for the "All Results" tab
                    title: 'All Results',
                    tabName: 'All Results',
                }),
                initialData: {
                    total: searchSections.reduce((acc, section) => acc + section.initialData.total, 0),
                    from: 0,
                    pageSize: 10,
                    culture: searchSections[0]?.initialData?.culture || 'en',
                    items: [],
                },
            },
            ...searchSections.filter(({ initialData }, i) => initialData?.items.length > 0),
        ];
    }, [searchSections]);
    const tabs = processedTabs.map((section, i) => ({
        key: i.toString(),
        label: section.tabName,
        onClick: () => {
            setIsInitialLoad(false);
            setActiveTab(i);
        },
        dataTestId: `search-listing-tab-${i}`,
        className: 'text-nowrap px-1.5 sm:px-0 sm:text-wrap',
    }));
    // Considering that All results is always inclusive.
    const hasContent = tabs.length > 1;
    useEffect(() => {
        const hash = window.location.hash.replace('#tab=', '');
        const tabIndex = parseInt(hash, 10);
        setActiveTab(isNaN(tabIndex) || tabIndex < 0 || tabIndex >= processedTabs.length ? 0 : tabIndex);
    }, [processedTabs.length]);
    useEffect(() => {
        if (activeTab === null || activeTab >= processedTabs.length)
            return;
        const section = processedTabs[activeTab];
        setActiveSection(section);
        updateHash(activeTab);
        // Only focus if this is not the initial load
        if (!isInitialLoad) {
            setTimeout(() => {
                const resultsContainer = section.contentType
                    ? document.querySelector(`#search-results-${section.contentType}-${activeTab}`)
                    : document.querySelector(`#search-results-all`);
                focusFirstFocusableIn(resultsContainer);
            }, 350);
        }
    }, [activeTab, processedTabs, updateHash, isInitialLoad]);
    useEffect(() => {
        if (!hasContent && query) {
            dataLayerPush('noMatchSearchResults', {
                search_query: query,
                search_location: 'Search Suggestions',
            });
        }
    }, [hasContent, query]);
    // Render a section title with optional count
    const renderSectionTitle = (title, count) => (_jsx("div", { className: "mb-1.5 md:mb-2", "data-testid": "search-section-title", children: _jsxs(Typography, { variant: "subtitle-md", as: "h2", className: "flex items-center gap-x-0.5", children: [title, count !== undefined && (_jsx("div", { className: "bg-symbol-primary px-0.25 text-center type-body-regular-book text-text-contrast", children: _jsx("span", { className: "ml-[3.5px]", "data-testid": "search-count", children: count }) }))] }) }));
    const renderAllResults = () => {
        const contentSections = processedTabs.filter((section) => section.tabName !== 'All Results');
        if (!contentSections || contentSections.length === 0)
            return null;
        const isExtraCard = (item) => !!item.__isExtra;
        const getExtraCard = (tabIdx, label) => (_jsx("div", { className: "flex h-full w-full items-center justify-center bg-ui-specific-glass-light", children: _jsx("div", { className: "flex h-full w-full flex-col items-center justify-center", children: _jsx(Button, { variant: "outline", children: (props) => (_jsx("button", { ...props, onClick: () => {
                            setIsInitialLoad(false);
                            setActiveTab(tabIdx);
                        }, "data-testid": "button-show-all", onFocus: () => setIsShowAllFocused(true), onBlur: () => setIsShowAllFocused(false), children: label })) }) }) }, "extra-card"));
        return (_jsx("div", { className: "flex flex-col gap-1.5", "data-testid": "search-all-results", children: contentSections.map((section, idx) => {
                const { initialData: { items = [], total = 0 }, contentType, } = section;
                if (items.length === 0 || !contentType)
                    return null;
                // Find the tab index for this section (skip the All Results tab)
                const tabIdx = processedTabs.findIndex((t) => t.tabName === section.tabName);
                // Prepare items with extra card if not all items are fetched
                const hasMoreCards = total > items.length;
                const itemsWithExtra = hasMoreCards ? [...items, { __isExtra: true }] : items;
                return (_jsxs("div", { className: "border-b border-border-muted pb-2.5 last:border-0", children: [section.title && renderSectionTitle(section.title, items.length), _jsx(Carousel, { items: itemsWithExtra, minimumSlides: 5, loop: false, onAdditionalDataLayerProps: (item) => {
                                if (!('__isExtra' in item) && item.dataObject?.brandName) {
                                    return {
                                        brand_name: item.dataObject.brandName,
                                        carousel_type: 'Search',
                                    };
                                }
                                return {
                                    carousel_type: 'Search',
                                };
                            }, onSlideClassName: () => clsx('md:!w-[calc((100%/4)+(0.75rem/2))] md:!pr-1.5'), onSlideRender: ({ item, index, accessibilityProps }) => {
                                if (isExtraCard(item)) {
                                    return getExtraCard(tabIdx, showAllLabel);
                                }
                                return (_jsx(SearchCard, { index: index, item: item, contentType: contentType, accessibilityProps: accessibilityProps, parentComponentName: "Search" }));
                            }, options: {
                                centeredSlides: true,
                                breakpoints: {
                                    832: {
                                        centeredSlides: false,
                                    },
                                },
                            }, labels: {
                                previousLabel: 'Previous',
                                nextLabel: 'Next',
                                previousIcon: 'arrow-left',
                                nextIcon: 'arrow-right',
                                slideProgressLabel: 'Slide {0} of {1}', // TODO: Replace these with a BE labels
                            }, focusedLastSlide: isShowAllFocused, children: ({ slides, nextButton, previousButton, pagination }) => (_jsxs(_Fragment, { children: [_jsx("div", { className: "mb-1.5 flex", children: slides }), _jsxs("div", { className: "flex items-center justify-between gap-1", children: [pagination, _jsxs("div", { className: "flex gap-1.5 md:gap-0.75", children: [previousButton, nextButton] })] })] })) }, `carousel-${contentType}-${idx}`)] }, `all-section-${idx}`));
            }) }));
    };
    const renderTitle = (title) => {
        return (_jsxs(Typography, { className: "mb-1", variant: "heading-xl", as: "h1", "data-testid": "search-listing-title-no-results", "aria-live": "polite", children: [title, " ", query && _jsxs("span", { className: "text-text-secondary", children: [" \u2018", query, "\u2019"] })] }));
    };
    const renderResults = () => {
        if (!activeSection)
            return null;
        return (_jsxs("div", { className: "mt-2", children: [_jsx("div", { id: "search-results-all", className: clsx(activeSection.tabName === allResult.tabName ? 'block' : 'hidden'), children: renderAllResults() }), processedTabs.map((section, index) => (_jsx("div", { id: `search-results-${section.contentType}-${index}`, className: clsx(activeSection.contentType === section.contentType ? 'block' : 'hidden'), children: _jsx(SearchSection, { contentType: section.contentType, initialData: section.initialData, searchEndpoint: searchEndpoint, parentPage: parentPage }) }, `search-section-${section.contentType}-${index}`)))] }));
    };
    return (_jsxs(SectionWrapper, { "data-testid": "search-listing-organism", "data-epi-block-id": componentID, centreTheme: centreTheme, topPadding: topPadding, bottomPadding: bottomPadding, anchorNavTitle: anchorNavTitle, theme: theme, className: "overflow-x-hidden bg-surface-primary", enableAnimations: true, children: [_jsxs("div", { className: "relative z-10", children: [hasContent ? (_jsxs(_Fragment, { children: [renderTitle(title), hasContent && (_jsx("div", { className: "-mx-site-padding mt-2 mb-1.5 flex gap-x-[1px] max-sm:overflow-x-auto sm:w-full md:mx-0", children: _jsx(SegmentedControl, { selectedItemKey: activeTab?.toString() || '0', items: tabs, className: "w-full bg-surface-primary" }) }))] })) : null, !hasContent ? (_jsx(SearchNoResults, { items: noResult?.items, description: noResult.description, title: renderTitle(noResult.title) })) : (renderResults())] }), _jsx("div", { className: "w-full", children: !hideBrandKeylines && (_jsx(BrandKeylineBackground, { className: "absolute z-0 max-sm:top-[-50%] md:[&>div:first-child]:top-[5%] md:[&>div:first-child]:right-[-30%] md:[&>div:first-child]:left-[initial] md:[&>div:first-child]:scale-70 [&>div:last-child]:hidden", duration: 3000 }, activeTab)) })] }));
};
export default SearchListing;
